import { memo, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { userState, initialState, AdminUser } from '@/store/user';
import { getAdminUser } from '@/pages/user/login/service';
import { getToken, setAdminUser, clearSession } from '@/utils/localToken';
import PageLoading from '@/components/PageLoading';
import settings from '@/config/settings';
import { mapPermissionPathListToFunctionList } from '@/utils/permission';

export interface SecurityLayoutProps { children: React.ReactNode; }

export default memo(({ children }: SecurityLayoutProps) => {
  const setUser = useSetRecoilState(userState);
  const location = useLocation();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [authError, setAuthError] = useState('');
  const token = getToken();
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === localStorage && (event.key === settings.siteTokenKey || event.key === null)) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  useEffect(() => {
    let active = true;
    setStatus('loading');
    setUser(initialState);
    if (!token) return () => { active = false; };
    getAdminUser().then((response) => {
      if (!active || getToken() !== token) return;
      const adminUser: AdminUser = response.result?.adminUser ?? response.result;
      if (!adminUser?.id || !adminUser?.userName) throw new Error('用户信息格式异常');
      adminUser.functionList = mapPermissionPathListToFunctionList(adminUser.permissionPathList ?? []);
      setAdminUser(adminUser);
      setUser({ id: adminUser.id, name: adminUser.userName, avatar: '',
        roles: adminUser.admin ? ['admin'] : ['user'], adminUser });
      setStatus('ready');
    }).catch((error) => {
      if (!active || (getToken() && getToken() !== token)) return;
      // A cached token alone does not establish a usable session. Let the user
      // sign in again even when validation fails with a non-401 response.
      clearSession();
      const response = error?.response;
      setAuthError(!response && error?.message !== '用户信息格式异常'
        ? '账号服务暂时无法连接，请稍后重新登录。'
        : response?.status >= 500
          ? '账号服务暂时异常，请稍后重新登录。'
          : '登录状态验证失败，请重新登录。');
      setStatus('error');
    });
    return () => { active = false; };
  }, [token, setUser]);
  if (!token || status === 'error') return <Navigate to={`/user/login?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`} state={{ authError }} replace />;
  if (status === 'loading') return <PageLoading />;
  return <>{children}</>;
});
